// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export type PushStatus =
  | 'loading'
  | 'unsupported'
  | 'denied'
  | 'not-subscribed'
  | 'subscribing'
  | 'subscribed'
  | 'error';

const getVapidPublicKey = async (): Promise<string> => {
  try {
    const res = await fetch('/api/notifications/public-key');
    if (res.ok) {
      const data = await res.json();
      if (data.publicKey) return data.publicKey;
    }
  } catch { /* ignora */ }
  return import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
};

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<PushStatus>('loading');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setStatus('unsupported');
        return;
      }
      const permission = Notification.permission;
      if (permission === 'denied') {
        setStatus('denied');
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await (reg as any).pushManager?.getSubscription();
        if (existing) {
          setSubscription(existing);
          setStatus('subscribed');
        } else {
          setStatus('not-subscribed');
        }
      } catch {
        setStatus('not-subscribed');
      }
    };
    check();
  }, []);

  const subscribe = useCallback(async () => {
    if (!user) {
      setError('Você precisa estar logado para ativar notificações');
      return false;
    }
    setStatus('subscribing');
    setError(null);
    try {
      let reg = await navigator.serviceWorker.getRegistration('/pwa-sw.js');
      if (!reg) {
        reg = await navigator.serviceWorker.register('/pwa-sw.js', { scope: '/' });
      }
      await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        setError('Permissão de notificação negada. Habilite nas configurações do navegador.');
        return false;
      }

      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) throw new Error('VAPID public key não configurada. Configure VITE_VAPID_PUBLIC_KEY.');

      const pm = (reg as any).pushManager;
      const sub = await pm.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      const saveRes = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON(), userId: user.id })
      });

      if (!saveRes.ok) {
        const errData = await saveRes.json().catch(() => ({}));
        throw new Error(errData.error || 'Erro ao salvar subscription no servidor');
      }

      setSubscription(sub);
      setStatus('subscribed');
      return true;
    } catch (err: any) {
      console.error('[usePushNotifications] Erro:', err);
      setError(err?.message || 'Erro ao ativar notificações');
      setStatus('error');
      return false;
    }
  }, [user]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    try {
      await subscription.unsubscribe();
      setSubscription(null);
      setStatus('not-subscribed');
    } catch (err: any) {
      setError(err?.message || 'Erro ao desativar notificações');
    }
  }, [subscription]);

  return { status, subscription, error, subscribe, unsubscribe };
};
